import { useEffect, useState } from "react";
import { History, Music4 } from "lucide-react";

const STORAGE_KEY = "rrz-track-history";
const ARTWORK_KEY = "rrz-artwork-cache";
const MAX_HISTORY = 10;
const MAX_ARTWORK_CACHE = 100;

function loadHistory() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.slice(0, MAX_HISTORY) : [];
  } catch { return []; }
}

function saveHistory(list) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(list)); } catch { /* ignore */ }
}

function loadArtworkCache() {
  try {
    const raw = localStorage.getItem(ARTWORK_KEY);
    if (!raw) return {};
    const obj = JSON.parse(raw);
    return typeof obj === "object" && obj ? obj : {};
  } catch { return {}; }
}

function saveArtworkCache(cache) {
  // Trim to newest MAX_ARTWORK_CACHE entries (insertion order preserved by object in modern JS)
  const keys = Object.keys(cache);
  let trimmed = cache;
  if (keys.length > MAX_ARTWORK_CACHE) {
    const keep = keys.slice(keys.length - MAX_ARTWORK_CACHE);
    trimmed = Object.fromEntries(keep.map((k) => [k, cache[k]]));
  }
  try { localStorage.setItem(ARTWORK_KEY, JSON.stringify(trimmed)); } catch { /* ignore */ }
}

async function fetchArtwork(raw) {
  try {
    const r = await fetch(`https://itunes.apple.com/search?term=${encodeURIComponent(raw)}&media=music&entity=song&limit=1`);
    if (!r.ok) return null;
    const j = await r.json();
    const hit = j?.results?.[0];
    return hit?.artworkUrl100
      ? hit.artworkUrl100.replace("100x100", "512x512")
      : null;
  } catch { return null; }
}

function parseTrack(raw) {
  const s = (raw || "").trim();
  if (!s) return { artist: "", title: "" };
  if (s.includes(" - ")) {
    const [left, ...rest] = s.split(" - ");
    const right = rest.join(" - ").trim();
    if (left && right) {
      return left.length <= right.length
        ? { artist: left.trim(), title: right }
        : { artist: right, title: left.trim() };
    }
  }
  return { artist: "", title: s };
}

function fmtTime(ts) {
  try {
    return new Date(ts).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

export default function TrackHistory() {
  const [history, setHistory] = useState(() => loadHistory());
  const [artworkMap, setArtworkMap] = useState(() => loadArtworkCache());

  useEffect(() => {
    const onNP = (ev) => {
      const detail = ev?.detail;
      const raw = (detail?.title || "").trim();
      if (!raw) return;
      setHistory((prev) => {
        // dedupe: if the newest entry is the same track, skip
        if (prev.length > 0 && prev[0].raw?.toLowerCase() === raw.toLowerCase()) return prev;
        const { artist, title } = parseTrack(raw);
        const entry = { raw, artist, title, ts: Date.now() };
        const next = [entry, ...prev].slice(0, MAX_HISTORY);
        saveHistory(next);
        return next;
      });
    };
    window.addEventListener("rrz:now-playing", onNP);
    return () => window.removeEventListener("rrz:now-playing", onNP);
  }, []);

  // Resolve iTunes artwork for any history entry missing one
  useEffect(() => {
    let cancelled = false;
    const missing = history.filter((h) => {
      const key = h.raw?.toLowerCase();
      return key && !(key in artworkMap);
    });
    if (missing.length === 0) return;
    (async () => {
      const next = { ...artworkMap };
      let changed = false;
      for (const h of missing) {
        const key = h.raw.toLowerCase();
        if (cancelled) return;
        const url = await fetchArtwork(h.raw);
        next[key] = url; // cache hits and misses (null)
        changed = true;
      }
      if (!cancelled && changed) {
        saveArtworkCache(next);
        setArtworkMap(next);
      }
    })();
    return () => { cancelled = true; };
  }, [history, artworkMap]);

  if (history.length === 0) return null;

  return (
    <section
      id="storico"
      data-testid="track-history"
      className="max-w-6xl mx-auto px-5 sm:px-8 py-8 sm:py-12 border-b border-[#332f2b]"
    >
      <div className="flex items-center gap-3 mb-5">
        <span className="w-9 h-9 rounded-lg bg-[#1a1816] border border-[#332f2b] flex items-center justify-center text-[#FF6B00]">
          <History size={18} />
        </span>
        <div>
          <h2 className="font-anton uppercase text-2xl sm:text-3xl text-[#F5F2EB] tracking-wide leading-none">
            Appena passati
          </h2>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#69635c] mt-1">
            Ultimi {history.length} brani
          </p>
        </div>
      </div>

      <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory scrollbar-thin">
        {history.map((h, i) => {
          const cover = artworkMap[h.raw?.toLowerCase()];
          return (
            <article
              key={`${h.ts}-${i}`}
              data-testid={`history-item-${i}`}
              className="snap-start shrink-0 w-[220px] rounded-xl border border-[#332f2b] bg-[#1a1816] p-3 hover:border-[#FF6B00]/50 transition-colors"
            >
              <div className="flex items-start gap-2.5">
                <span className="shrink-0 w-12 h-12 rounded-md overflow-hidden bg-[#12100e] border border-[#332f2b] flex items-center justify-center text-[#E1A140]">
                  {cover ? (
                    <img
                      src={cover}
                      alt=""
                      loading="lazy"
                      className="w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  ) : (
                    <Music4 size={16} />
                  )}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-oswald uppercase text-sm leading-tight text-[#F5F2EB] truncate">
                    {h.title || h.raw}
                  </p>
                  {h.artist && (
                    <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#A39A8F] truncate mt-0.5">
                      {h.artist}
                    </p>
                  )}
                  <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#69635c] mt-1.5">
                    {fmtTime(h.ts)}
                  </p>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
